import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, Check, Gift, Send, Users, Sparkles, Printer } from "lucide-react";
import { toast } from "sonner";

export default function Referrals() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/referral/stats");
                setStats(data);
            } catch (e) {
                console.error(e);
            }
        })();
    }, []);

    const link = stats
        ? `${window.location.origin}/?ref=${stats.referral_code}`
        : "";

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
            toast.success("Link copied to clipboard");
            setTimeout(() => setCopied(false), 1800);
        } catch {
            toast.error("Copy failed");
        }
    };

    const share = async () => {
        if (!navigator.share) {
            copy();
            return;
        }
        try {
            await navigator.share({
                title: "Flex Line — second phone number",
                text: "I'm using Flex Line for a private second phone number. Join with my link and get $20 off your bill when you activate FlexLine service.",
                url: link,
            });
        } catch {}
    };

    return (
        <div className="space-y-8" data-testid="referrals-page">
            <div className="flex items-end justify-between gap-6">
                <div>
                    <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-2">
                        — Refer & earn
                    </p>
                    <h1 className="font-display text-4xl font-light tracking-tight">
                        Give a friend service,
                        <br />
                        <span className="font-serif-italic">get $20 off your bill.</span>
                    </h1>
                </div>
                <a
                    href="/poster"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="open-poster-link"
                    className="h-10 px-4 bg-espresso text-[#FAF5FF] rounded-sm text-sm inline-flex items-center gap-2 hover:bg-black shrink-0"
                >
                    <Printer className="w-4 h-4" /> Marketing poster
                </a>
            </div>

            {/* Reward summary */}
            <div className="grid md:grid-cols-3 gap-px bg-[#DDD6FE] border border-[#DDD6FE]">
                <StatBlock
                    label="Friends referred"
                    value={stats?.referral_count ?? 0}
                    icon={Users}
                    testid="ref-stat-count"
                />
                <StatBlock
                    label="Bill credit earned"
                    value={`$${(((stats?.bill_credit_earned_cents) ?? 0) / 100).toFixed(0)}`}
                    icon={Gift}
                    testid="ref-stat-credits"
                    highlight
                />
                <StatBlock
                    label="Reward per activation"
                    value={`$${(((stats?.reward_bill_credit_cents) ?? 2000) / 100).toFixed(0)} off`}
                    icon={Sparkles}
                    testid="ref-stat-reward"
                />
            </div>
            {(stats?.bill_credit_pending_cents ?? 0) > 0 && (
                <div className="rounded-sm bg-[#F5F0FF] border border-[#4C1D95]/20 px-5 py-3 text-sm text-[#4C1D95]" data-testid="ref-pending-credit">
                    You have <span className="font-semibold">${((stats.bill_credit_pending_cents) / 100).toFixed(0)}</span> in referral credit waiting — it'll come off automatically on your next bill.
                </div>
            )}

            {/* Share block */}
            <div className="border border-[#DDD6FE] rounded-sm bg-paper p-8">
                <div className="grid lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-5">
                        <h2 className="font-display text-2xl font-light tracking-tight">
                            Your invite link
                        </h2>
                        <p className="text-sm text-zinc-600 mt-2">
                            When a friend signs up with your link and{" "}
                            <span className="font-medium text-emerald-deep">activates FlexLine service</span>,
                            you get a <span className="font-medium text-emerald-deep">$20 bill credit</span>{" "}
                            applied straight to your next bill. They start with bonus SMS credits too.
                        </p>
                        <p className="text-xs text-zinc-500 mt-3 font-mono">
                            Your code: <span className="text-emerald-deep font-medium" data-testid="ref-code-display">{stats?.referral_code || "…"}</span>
                        </p>
                    </div>
                    <div className="lg:col-span-7 space-y-3">
                        <label className="font-mono text-[10px] uppercase text-zinc-500">Invite URL</label>
                        <div className="flex gap-2">
                            <Input
                                readOnly
                                value={link}
                                className="font-mono text-sm rounded-sm bg-cream/40 border-[#DDD6FE]"
                                data-testid="ref-link-input"
                            />
                            <Button
                                onClick={copy}
                                data-testid="ref-copy-btn"
                                className="rounded-sm h-10 bg-espresso text-[#FAF5FF] hover:bg-black px-4"
                            >
                                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            </Button>
                            <Button
                                onClick={share}
                                data-testid="ref-share-btn"
                                variant="outline"
                                className="rounded-sm h-10 border-[#DDD6FE] px-4"
                            >
                                <Send className="w-4 h-4 mr-2" /> Share
                            </Button>
                        </div>
                        <div className="grid grid-cols-3 gap-2 pt-3">
                            <a
                                href={`mailto:?subject=${encodeURIComponent("Try Flex Line with me")}&body=${encodeURIComponent("I'm using Flex Line for a private second phone number. Join with my link and get $20 off your bill when you activate FlexLine service: " + link)}`}
                                data-testid="ref-email-btn"
                                className="text-center text-sm py-2 border border-[#DDD6FE] rounded-sm hover:bg-cream/60 font-mono"
                            >
                                Email
                            </a>
                            <a
                                href={`sms:?&body=${encodeURIComponent("Try Flex Line with me — get $20 off your bill when you activate FlexLine service: " + link)}`}
                                data-testid="ref-sms-btn"
                                className="text-center text-sm py-2 border border-[#DDD6FE] rounded-sm hover:bg-cream/60 font-mono"
                            >
                                Text
                            </a>
                            <a
                                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent("I'm using @flexline_app for a private second phone number. Join with my link and get $20 off your bill when you activate FlexLine service: " + link)}`}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="ref-twitter-btn"
                                className="text-center text-sm py-2 border border-[#DDD6FE] rounded-sm hover:bg-cream/60 font-mono"
                            >
                                Twitter
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recent referrals */}
            <div className="border border-[#DDD6FE] rounded-sm bg-paper p-6">
                <h3 className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-4">
                    Recent referrals
                </h3>
                {stats?.recent?.length > 0 ? (
                    <ul className="divide-y divide-[#DDD6FE]/60">
                        {stats.recent.map((r) => (
                            <li key={r.referral_id} className="py-3 flex items-center justify-between" data-testid={`ref-row-${r.referral_id}`}>
                                <div>
                                    <div className="text-sm font-medium">{r.name}</div>
                                    <div className="font-mono text-[10px] text-zinc-500">
                                        joined {new Date(r.joined_at).toLocaleDateString()}
                                    </div>
                                </div>
                                <span className="font-mono text-xs text-emerald-deep">{r.reward_cents ? `+$${(r.reward_cents / 100).toFixed(0)} bill credit` : `+${r.reward_sms} SMS`}</span>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div className="py-10 text-center text-sm text-zinc-500">
                        No referrals yet. Share your link to start earning credits.
                    </div>
                )}
            </div>
        </div>
    );
}

function StatBlock({ label, value, icon: Icon, highlight, testid }) {
    return (
        <div className="bg-paper p-6" data-testid={testid}>
            <div className="flex items-start justify-between">
                <p className="font-mono text-[11px] uppercase text-zinc-500">{label}</p>
                <Icon className={`w-4 h-4 ${highlight ? "text-emerald-deep" : "text-zinc-400"}`} />
            </div>
            <div className="font-display text-5xl font-light mt-3 tracking-tight">{value}</div>
        </div>
    );
}
